import * as React from "react";
import PropTypes from "prop-types";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { visuallyHidden } from "@mui/utils";
import ScrollEffect from "../../effects/ScrollEffect";
import { Download, Eye, FunnelSimple, Pencil, PlusCircle, X } from "phosphor-react";
import Select from "../Inputs/Select";
import QueueStyle from "../Styled/Queue.style";
import {
  useModalHistory,
  ACTIONS as MODAL_ACTIONS,
} from "../../context/ModalHistoryProvider";
import UpdateDealModal from "../Modals/UpdateDeal/UpdateDealModal";
import LoadEffect from "../../effects/LoadEffect";
import { CircularProgress, Skeleton } from "@mui/material";
import StackStyle from "../Styled/Stack.style";
import useType from "../../utils/hooks/useType";
import Popup from "../Popup/Popup";
import { CancelButton } from "../Controls/CancelButton";
import { ConfirmButton } from "../Controls/ConfirmButton";
import { useRef } from "react";
import styled from "styled-components";
import { inputWithStyle } from "../../HOC/inputWithStyle";
import { inputWithFloatingLabel } from "../../HOC/inputWithFloatingLabel";
import InputBase from "../Inputs/InputBase";
import useArray from "../../utils/hooks/useArray";
import { FILTERS } from "../../utils/constants/FILTERS";
import {
  useDealsContext,
  ACTIONS as DEAL_ACTIONS,
} from "../../context/DealsProvider";
import ExtendDealsModal from "../Modals/ExtendDeals/ExtendDealsModal";
import colors from "../../utils/constants/COLORS";
import ViewAndEditModal from "../Modals/ViewAndEdit/ViewAndEditModal";
import {
  CONDITIONS,
  DEAL_BY_BRAND_ID,
  DEAL_EDIT_ACTIONS,
  DEAL_STATUSES,
  DEAL_TYPES,
  USER_KEY,
} from "../../utils/constants/SYSTEM";
import api from "../../api/api";
import {
  ALERT_ACTIONS,
  ALERT_STATUSES,
  useAlert,
} from "../../context/AlertProvider";
import { get } from "../../utils/helpers/storage";
import parseDecimal from "../../utils/helpers/parseDecimal";
import { useEffect } from "react";
import hasNoValue from "../../utils/helpers/hasNoValue";
import { convertJsonToExcel } from "../../utils/helpers/excelToJson";
import useToast, { TOAST_TYPES } from "../../utils/hooks/useToast";
import { NavLink } from "react-router-dom";

function descendingComparator(a, b, orderBy) {
  if (
    (parseFloat(b[orderBy]) || b[orderBy]) <
    (parseFloat(a[orderBy]) || a[orderBy])
  ) {
    return -1;
  }
  if (
    (parseFloat(b[orderBy]) || b[orderBy]) >
    (parseFloat(a[orderBy]) || a[orderBy])
  ) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const StyledTableCell = styled(TableCell)`
  span {
    display: block;
    width: max-content;
    font-size: x-small;
    color: GrayText;
  }
`;

function EnhancedTableHead(props) {
  const {
    headers,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    pushEnd,
    selected,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  console.log('selected', selected);

  return (
    <TableHead>
      <TableRow>
        <TableCell>
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all deals",
            }}
          />
        </TableCell>
        <TableCell sx={{fontSize: "x-small", color: "GrayText"}}>
          {numSelected < 2 ? "Edit" : numSelected > 1 && "Bulk edit"}
          {numSelected > 0 && (
            <Tooltip title="Edit selected deal(s)">
              <IconButton
                onClick={() => pushEnd(<UpdateDealModal items={selected} scopeID={"preloaded_Deals_ID"} />)}
              >
                <Pencil weight="thin" size={14} />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <StyledTableCell>
          <span>View & Edit</span>
        </StyledTableCell>
        <StyledTableCell>
          <span>Download</span>
        </StyledTableCell>
        {headers?.map((header) => (
          <StyledTableCell
            key={header.id}
            padding={"checkbox"}
            sortDirection={orderBy === header.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === header.id}
              direction={orderBy === header.id ? order : "asc"}
              onClick={createSortHandler(header.id)}
              sx={{ textTransform: "capitalize" }}
            >
              {orderBy === header.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
              {header.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}

      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  headers: PropTypes.array.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const InputWithFloatingLabel = inputWithStyle(
  inputWithFloatingLabel(InputBase)
);

const EnhancedTableToolbar = (props) => {
  const { numSelected, headers } = props;
  const {
    array: filtersList,
    setArray: setFiltersList,
    update: updateFiltersList,
    remove: removeFiltersList,
  } = useArray([{ label: "", operator: "contains", value: "" }]);

  const [showFilters, setShowFilters] = useType("boolean", false);
  const [{ dealsFiltered, dealRows, condition }, dispatchDeals] = useDealsContext();

  const anchorRef = useRef();

  const isFilterMissing =
    filtersList.filter((f) => !f.label || !f.operator || !f.value).length > 0;

  return (
    <Toolbar
      sx={{
        minHeight: "unset !important",
        pl: { sm: 2 },
        pr: { sm: 2 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      <Typography
        sx={{ flex: "1 1 100%" }}
        variant="h6"
        id="tableTitle"
        component="div"
      >
        Deals {numSelected > 0 && <>({numSelected}) selected</>} {dealsFiltered?.length > 0 && <>Filtered</>} 
        {dealRows?.length > 1 && (
          <IconButton title={`Bulk download ${condition} deals (${dealRows?.length})`} onClick={() => convertJsonToExcel(dealRows, `Deal_Portal_Bulk_${condition}`, condition)}>
            <Download weight="thin" size={18} />
          </IconButton>
        )}
      </Typography>

      {headers?.length > 0 && (
        <>
          <Tooltip title="Filter deals">
            <IconButton onClick={() => setShowFilters(true)}>
              <FunnelSimple
                ref={anchorRef}
                weight="regular"
                size={22}
              />
            </IconButton>
          </Tooltip>
          <Popup
            open={showFilters}
            anchor={anchorRef.current}
            title={
              <QueueStyle>
                <h5>Filters ({filtersList?.filter(f => f.label && f.operator && f.value)?.length})</h5>
                <Tooltip title="Clear all filters">
                  <IconButton
                    onClick={() => {
                      setFiltersList([
                        { label: "", operator: "contains", value: "" },
                      ]);
                      dispatchDeals({
                        type: DEAL_ACTIONS.SET_FILTERS_LIST,
                        payload: [],
                      });
                    }}
                  >
                    <X weight="thin" size={18} />
                  </IconButton>
                </Tooltip>
              </QueueStyle>
            }
            body={
              <StackStyle alignContent="start" gap="10px" height="50vh">
                {filtersList.map((f, index) => (
                  <QueueStyle
                    justifyContent="space-between"
                    gap="15px"
                    key={index}
                  >
                    <Select
                      options={headers.map((h) => h.label)}
                      value={f.label}
                      handleChange={(label) => {
                        updateFiltersList(index, {
                          ...f,
                          label: label.replace(/\s/g, "_"),
                        });
                      }}
                      label={`Filter item ${index + 1}`}
                      id={`filter-item-${index + 1}`}
                      width="220px"
                    />
                    <Select
                      options={Object.keys(FILTERS)}
                      value={f.operator}
                      handleChange={(operator) =>
                        updateFiltersList(index, { ...f, operator })
                      }
                      label={`Filter operator ${index + 1}`}
                      id={`filter-operator-${index + 1}`}
                      width="150px"
                    />
                    <InputWithFloatingLabel
                      value={f.value}
                      onChange={(e) =>
                        updateFiltersList(index, {
                          ...f,
                          value: e.target.value,
                        })
                      }
                      label={`Filter value ${index + 1}`}
                      id={`filter-value-${index + 1}`}
                      width="220px"
                    />
                    {filtersList.length > 1 && (
                      <Tooltip title="Remove filter">
                        <IconButton onClick={() => removeFiltersList(index)}>
                          <X weight="thin" size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </QueueStyle>
                ))}
                <QueueStyle justifyContent="center">
                  <Tooltip title="Add new filter">
                    <IconButton
                      onClick={() =>
                        setFiltersList([
                          ...filtersList,
                          { label: "", operator: "contains", value: "" },
                        ])
                      }
                    >
                      <PlusCircle weight="thin" size={22} />
                    </IconButton>
                  </Tooltip>
                </QueueStyle>
              </StackStyle>
            }
            action={
              <>
                <CancelButton onClick={() => setShowFilters(false)} />
                <ConfirmButton
                  onClick={() => {
                    dispatchDeals({
                      type: DEAL_ACTIONS.SET_FILTERS_LIST,
                      payload: filtersList,
                    });
                    setShowFilters(false);
                  }}
                  disabled={isFilterMissing}
                />
              </>
            }
            variant="info"
            onClose={() => setShowFilters(false)}
          />
        </>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  headers: PropTypes.array.isRequired,
};

export default function TableCheckboxSelect({ headers, rows }) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("deal_Status");
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const id = React.useId();

  const [{ isLoading, condition }, dispatchDeals] = useDealsContext();

  const [, dispatchModalHistory] = useModalHistory();
  const pushEnd = (modal) =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.PUSH_END,
      payload: { value: modal },
    });
  const popEnd = () =>
    dispatchModalHistory({
      type: MODAL_ACTIONS.POP_END,
    });
  const [addToast] = useToast();

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.preloaded_Deals_ID);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);

    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleDealUpdate = (value, row) => {
    switch (value) {
      case DEAL_EDIT_ACTIONS.TURN_ON:
        (async () => {
          const updateEndDateResp = await api.updatePreloadedNewDealDealStatus({
            ID: row.preloaded_Deals_ID,
            DealStatus: "QuoteApproved",
          });

          if (updateEndDateResp?.success) {
            // dispatchDeals({ type: DEAL_ACTIONS.REFETCH_DEALS, payload: true });
            addToast(TOAST_TYPES.SUCCESS, "Successfully turned on deal.");
          } else {
            addToast(TOAST_TYPES.ERROR, "Error turning on deal.");
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.TURN_OFF:
        (async () => {
          const updateEndDateResp = await api.updatePreloadedNewDealDealStatus({
            ID: row.preloaded_Deals_ID,
            DealStatus: "QuoteStopped",
          });

          if (updateEndDateResp?.success) {
            // dispatchDeals({ type: DEAL_ACTIONS.REFETCH_DEALS, payload: true });
            addToast(TOAST_TYPES.SUCCESS, "Successfully turned off deal.");
          } else {
            addToast(TOAST_TYPES.ERROR, "Error turning off deal.");
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.DUPLICATE:
        (async () => {
          if (row?.plD_Type === DEAL_TYPES.INSTALMENT_PRICE) {
            const schemeDealInfo = await api.getNewSchemeDealInfo(
              row?.preloaded_Deals_ID
            );

            console.log("schemeDealInfo", schemeDealInfo);

            const schemeDealData = {
              UserID: parseInt(get(USER_KEY)?.userID),
              CarID: schemeDealInfo?.car_ID,
              DealStatus:
                schemeDealInfo?.deal_Status === DEAL_STATUSES.ACTIVE
                  ? "QuoteApproved"
                  : "QuoteStopped",
              QuoteCreateDate: schemeDealInfo?.quote_Create_Date,
              QuoteReusableStartDate: schemeDealInfo?.quote_Reusable_Start_Date,
              QuoteReusableEndDate: schemeDealInfo?.quote_Reusable_End_Date,
              DealName: schemeDealInfo?.deal_Name,
              Notes: schemeDealInfo?.notes,
              SearchBarcode: schemeDealInfo?.search_Barcode,
              CashBack: parseDecimal(schemeDealInfo?.cash_Back),
              DealerSupport: parseDecimal(schemeDealInfo?.dealer_Support),
              Instalment: parseDecimal(schemeDealInfo?.instalment),
              RetailIncl: parseDecimal(schemeDealInfo?.retail_Incl),
              RateType: schemeDealInfo?.rate_Type,
              RatePercent: parseDecimal(schemeDealInfo?.rate_Percent),
              Deposit: parseDecimal(schemeDealInfo?.deposit),
              Period: parseInt(schemeDealInfo?.period) || 0,
              BalloonPercent: parseDecimal(schemeDealInfo?.balloon_Percent),
              BalloonAmount: parseDecimal(schemeDealInfo?.balloon_Amount),
              TotalCostOfCredit: parseDecimal(
                schemeDealInfo?.total_Cost_Of_Credit
              ),
              DealJSON: schemeDealInfo?.deal_JSON
            };
            console.log("schemeDealData", schemeDealData);

            const insertSchemeDealResp = await api.insertNewSchemeDeal(
              schemeDealData
            );

            if (insertSchemeDealResp?.success) {
              addToast(TOAST_TYPES.SUCCESS, "Successfully duplicated deal.");
            } else {
              addToast(TOAST_TYPES.ERROR, "Error duplicating deal.");
            }
          } else if (row?.plD_Type === DEAL_TYPES.LIST_PRICE) {
            const listPriceDealInfo = await api.getNewListPriceDealInfo(
              row?.preloaded_Deals_ID
            );

            console.log("listPriceDealInfo", listPriceDealInfo);

            const listPriceDealData = {
              UserID: parseInt(get(USER_KEY)?.userID),
              CarID: listPriceDealInfo?.car_ID,
              DealStatus:
                listPriceDealInfo?.deal_Status === DEAL_STATUSES.ACTIVE
                  ? "QuoteApproved"
                  : "QuoteStopped",
              QuoteCreateDate: listPriceDealInfo?.quote_Create_Date,
              QuoteReusableStartDate:
                listPriceDealInfo?.quote_Reusable_Start_Date,
              QuoteReusableEndDate: listPriceDealInfo?.quote_Reusable_End_Date,
              DealName: listPriceDealInfo?.deal_Name,
              Notes: listPriceDealInfo?.notes,
              SearchBarcode: listPriceDealInfo?.search_Barcode,
              CashBack: parseDecimal(listPriceDealInfo?.cash_Back),
              DealerSupport: parseDecimal(listPriceDealInfo?.dealer_Support),
              DealJSON: listPriceDealInfo?.deal_JSON
            };
            console.log("listPriceDealData", listPriceDealData);

            const insertListPriceDealResp = await api.insertNewListPriceDeal(
              listPriceDealData
            );

            if (insertListPriceDealResp?.success) {
              dispatchDeals({
                type: DEAL_ACTIONS.REFETCH_DEALS,
                payload: true,
              });
              addToast(TOAST_TYPES.SUCCESS, "Successfully duplicated deal.");
            } else {
              addToast(TOAST_TYPES.ERROR, "Error duplicating deal.");
            }
          }
        })();
        break;
      case DEAL_EDIT_ACTIONS.EXTEND:
        pushEnd(
          <ExtendDealsModal
            onSubmit={(newDate) => {
              (async () => {
                const updateEndDateResp =
                  await api.updatePreloadedNewDealEndDate({
                    ID: row.preloaded_Deals_ID,
                    EndDate: newDate,
                  });

                if (updateEndDateResp?.success) {
                  dispatchDeals({
                    type: DEAL_ACTIONS.REFETCH_DEALS,
                    payload: true,
                  });
                  addToast(TOAST_TYPES.SUCCESS, "Successfully updated end date.");
                } else {
                  addToast(TOAST_TYPES.ERROR, "Error updating end date.");
                }
              })();
              popEnd();
            }}
          />
        );
        break;

      default:
        break;
    }
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <EnhancedTableToolbar numSelected={selected.length} headers={headers} />
        <LoadEffect condition={!isLoading} fallback={<StackStyle justifyContent="center"><CircularProgress size={30} /></StackStyle>}>
          <TableContainer sx={{display: "flex", flexGrow: 1}}>
            <ScrollEffect width="100%" height="calc(100vh - 253.38px)">
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="Deals"
                size="small"
                padding="checkbox"
                stickyHeader
              >
                <EnhancedTableHead
                  headers={headers}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                  pushEnd={pushEnd}
                  selected={selected}
                />

                <TableBody>
                  {stableSort(rows, getComparator(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const isItemSelected = isSelected(row.preloaded_Deals_ID);
                      const labelId = `enhanced-table-checkbox-${index}`;

                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={row.preloaded_Deals_ID}
                          selected={isItemSelected}
                        >
                          <TableCell>
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              onClick={(event) =>
                                handleClick(event, row.preloaded_Deals_ID)
                              }
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <QueueStyle gap="10px">
                              <Select
                                defaultValue="Actions"
                                options={Object.values(DEAL_EDIT_ACTIONS)}
                                handleChange={(value) =>
                                  handleDealUpdate(value, row)
                                }
                              />
                            </QueueStyle>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View & edit deal">
                              <NavLink to={`/new/${row?.preloaded_Deals_ID}/${row?.plD_Type}`}>
                                <IconButton
                                  sx={{width: "100%", height: "100%", padding: "16px !important"}}
                                >
                                  <Eye weight="thin" size={18} />
                                </IconButton>
                              </NavLink>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <IconButton sx={{width: "100%", height: "100%", padding: "16px !important"}} onClick={() => convertJsonToExcel([row], `Deal_Portal_${row?.brand}_${row?.range}_${row?.model}_${condition}_${row?.plD_Type}`, `${condition}_${row?.plD_Type}`)}>
                              <Download weight="thin" size={18} />
                            </IconButton>
                          </TableCell>
                          {Object.keys(row).map((key, index) => (
                            <StyledTableCell
                              onClick={(event) =>
                                handleClick(event, row.preloaded_Deals_ID)
                              }
                              key={`${id}-${index}`}
                            >
                              {row[key]}
                            </StyledTableCell>
                          ))}

                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: 33 * emptyRows,
                      }}
                    >
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollEffect>
          </TableContainer>
        </LoadEffect>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, rows?.length]}
          component="div"
          count={rows?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
